import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { STATUS_PILL_ROW_COMPACT_CLASS } from "../../components/StatusPill";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { cn } from "../../lib/utils";
import { toast } from "sonner";
import {
  Heading1,
  Heading2,
  Heading3,
  Heading4,
  Info,
  List,
  ListCollapse,
  ListOrdered,
  ListTodo,
  Minus,
  Plus,
  Quote,
  Share2,
  Trash2,
  Type,
} from "lucide-react";
import NoteShareDialog from "../../components/NoteShareDialog";
import { FullScreenModal } from "../../components/FullScreenModal";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { PortalNotesRatioStat } from "../../components/PortalInventoryStat";
import { NoteRichEditor } from "../../components/NoteRichEditor";
import { notePreviewFromContent } from "../../lib/noteBlocks";
import { noteActivityDisplay } from "../../lib/noteActivity";

const EMPTY_FORM = {
  title: "",
  content: "",
  category: "general",
  status: "draft",
  owner: "",
};

const NOTE_STATUS_MENU = [
  { value: "draft", label: "INTERNAL" },
  { value: "archived", label: "ARCHIVED" },
];

const ADMIN_STATUS_FILTERS = [
  ["all", "All"],
  ["archived", "Archived"],
];

const NOTES_NAV_TEXT = "text-xs sm:text-sm";
const NOTES_CARD_TITLE = "text-sm sm:text-base";
const NOTES_BOARD_CARD_TITLE = "text-xs sm:text-sm";
const NOTES_BOARD_CARD_BODY = "text-[11px] sm:text-xs leading-snug";

const NOTE_FILTERS = new Set(ADMIN_STATUS_FILTERS.map(([key]) => key));

const NOTE_STATUS_LABELS = {
  draft: "Internal",
  shared: "Shared",
  archived: "Archived",
};

function normalizeNoteStatus(status) {
  if (!status) return "draft";
  const s = String(status).trim().toLowerCase();
  if (s === "ready") return "shared";
  if (s === "in progress") return "draft";
  if (s === "shared" || s === "archived") return s;
  return "draft";
}

function noteStatusPillTone(status) {
  return normalizeNoteStatus(status) === "shared" ? "ok" : "meta";
}

function sortNotesByCreatedDesc(rows) {
  return [...rows].sort(
    (a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime(),
  );
}

function NoteStatusPopover({ note, open, onOpenChange, onSelectStatus }) {
  const status = normalizeNoteStatus(note.status);
  const current = NOTE_STATUS_MENU.find((o) => o.value === status) || NOTE_STATUS_MENU[0];
  const tone = noteStatusPillTone(status);

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          className={cn(
            "t-pill inline-flex shrink-0 cursor-pointer border bg-transparent text-left font-[inherit] hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--t-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--t-bg)]",
            tone === "ok" && "t-pill-ok",
            tone === "meta" && "t-pill-meta",
            STATUS_PILL_ROW_COMPACT_CLASS,
          )}
          data-testid={`note-status-${note.id}`}
          aria-label="Change note status"
        >
          {current.label}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="z-[60] w-auto min-w-[12rem] neo-panel p-2 text-[var(--t-text)] t-glass"
        data-testid={`note-status-menu-${note.id}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col gap-1">
          {NOTE_STATUS_MENU.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={cn(
                "rounded-[var(--t-event-pill-radius)] px-3 py-2 text-left text-[10px] font-normal uppercase tracking-[0.16em] transition-colors hover:bg-black/[0.04]",
                opt.value === status && "border border-[var(--t-accent)] bg-[var(--t-accent)]/5 text-[var(--t-accent)]",
              )}
              onClick={(e) => {
                e.stopPropagation();
                onSelectStatus(note, opt.value);
                onOpenChange(false);
              }}
              data-testid={`note-status-option-${opt.value}-${note.id}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

const FORMAT_BLOCKS = [
  { key: "paragraph", label: "Text", icon: Type },
  { key: "h1", label: "Heading 1", icon: Heading1 },
  { key: "h2", label: "Heading 2", icon: Heading2 },
  { key: "h3", label: "Heading 3", icon: Heading3 },
  { key: "h4", label: "Heading 4", icon: Heading4 },
  { key: "bullet", label: "Bulleted list", icon: List },
  { key: "numbered", label: "Numbered list", icon: ListOrdered },
  { key: "todo", label: "To-do list", icon: ListTodo },
  { key: "toggle", label: "Toggle list", icon: ListCollapse },
  { key: "quote", label: "Quote", icon: Quote },
  { key: "callout", label: "Callout", icon: Info },
  { key: "divider", label: "Divider", icon: Minus },
];

function NoteFormatToolbar({ editorRef }) {
  return (
    <div
      className="flex w-full flex-wrap items-center gap-1 neo-panel px-2 py-1.5"
      data-testid="note-format-toolbar"
      role="toolbar"
      aria-label="Formatting. Shift+click or Shift+arrow to select multiple lines."
    >
      {FORMAT_BLOCKS.map((block) => {
        const Icon = block.icon;
        return (
          <button
            key={block.key}
            type="button"
            title={block.label}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => editorRef.current?.insertBlock(block.key)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-[var(--t-event-pill-radius)] text-[var(--t-meta)] transition-colors hover:bg-black/[0.05] hover:text-[var(--t-text)]"
            data-testid={`note-format-${block.key}`}
          >
            <Icon size={15} strokeWidth={1.4} className="shrink-0" />
          </button>
        );
      })}
    </div>
  );
}

const NOTE_MODAL_OVERLAY_CLASS =
  "max-lg:items-stretch max-lg:justify-center max-lg:px-2 max-lg:pb-2 max-lg:pt-[calc(var(--portal-mobile-header-h,68px)+0.5rem)]";

const NOTE_MODAL_PANEL_CLASS =
  "relative flex w-full max-w-[44rem] flex-col overflow-hidden min-h-0 max-lg:max-w-full max-lg:min-h-0 max-lg:h-[calc(100dvh-var(--portal-mobile-header-h,68px)-1rem)] max-lg:max-h-[calc(100dvh-var(--portal-mobile-header-h,68px)-1rem)] lg:min-h-[min(92dvh,56rem)] lg:max-h-[min(94dvh,58rem)] p-4 sm:p-6 md:p-8";

function noteEditorLabel(note) {
  return (note?.last_edited_by || note?.owner || "Taī").trim() || "Taī";
}

function NoteDetailMeta({ note }) {
  const activity = noteActivityDisplay(note);
  return (
    <div
      className="flex shrink-0 items-end justify-between gap-3 border-t border-[var(--t-border)] pt-4"
      data-testid="note-detail-meta"
    >
      <span className="text-[10px] leading-none tracking-[0.02em] text-[var(--t-meta)] font-light">
        Edited by {noteEditorLabel(note)}
      </span>
      <span className="text-[10px] leading-none tracking-[0.02em] text-[var(--t-meta)] font-light">
        {activity}
      </span>
    </div>
  );
}

function NoteDetailReadOnly({ note }) {
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden sm:gap-4" data-testid="note-detail-panel">
      <h2 className="shrink-0 font-heading text-xl sm:text-2xl tracking-tight">{note.title || "Untitled note"}</h2>
      <NoteRichEditor
        noteId={note.id}
        value={note.content || ""}
        onChange={() => {}}
        readOnly
        fillContainer
        className="min-h-0 flex-1"
      />
    </div>
  );
}

function NoteDetailEditor({ note, form, onFormChange }) {
  const editorRef = useRef(null);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden sm:gap-4" data-testid="note-detail-panel">
      <input
        className="shrink-0 w-full border-0 bg-transparent p-0 font-heading text-xl sm:text-2xl tracking-tight outline-none placeholder:text-[var(--t-meta)]"
        value={form.title}
        onChange={(e) => onFormChange({ title: e.target.value })}
        placeholder="Untitled note"
        data-testid="note-title-input"
      />
      <div className="shrink-0">
        <NoteFormatToolbar editorRef={editorRef} />
      </div>
      <NoteRichEditor
        ref={editorRef}
        className="min-h-0 flex-1"
        noteId={note.id}
        value={form.content}
        onChange={(content) => onFormChange({ content })}
        fillContainer
      />
    </div>
  );
}

function NoteDetailModal({ note, canEdit, form, onFormChange, onClose, onShare }) {
  const isShared = normalizeNoteStatus(note.status) === "shared";

  return (
    <FullScreenModal
      onClose={onClose}
      ariaLabel={note.title || "Note"}
      testid="note-detail-modal"
      overlayClassName={NOTE_MODAL_OVERLAY_CLASS}
      contentClassName={NOTE_MODAL_PANEL_CLASS}
    >
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {canEdit ? (
          <NoteDetailEditor note={note} form={form} onFormChange={onFormChange} />
        ) : (
          <NoteDetailReadOnly note={note} />
        )}
        </div>
        <NoteDetailMeta note={note} />
        {canEdit && onShare ? (
          <div className="mt-4 flex shrink-0 flex-wrap items-center justify-end gap-2">
            <button
              type="button"
              onClick={onShare}
              className="neo-cta"
              data-testid="note-share-with-client-btn"
            >
              <Share2 size={16} strokeWidth={1.4} />
              {isShared ? "Update" : "Share with client"}
            </button>
          </div>
        ) : null}
      </div>
    </FullScreenModal>
  );
}

function CreateNoteModal({ clientId, clientName = "", userName, isAdmin, onClose, onSaved }) {
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const trimmed = title.trim();
    if (!trimmed) {
      toast.error("Name your note");
      return;
    }
    setBusy(true);
    try {
      const url = isAdmin ? `/portal/notes?client_id=${clientId}` : "/portal/notes";
      const { data } = await api.post(url, {
        title: trimmed,
        content: "",
        category: "general",
        status: isAdmin ? "draft" : "shared",
        owner: userName || "",
      });
      toast.success("Note created");
      onSaved(data);
      onClose();
    } catch {
      toast.error("Could not create note");
    } finally {
      setBusy(false);
    }
  };

  return (
    <FullScreenModal
      onClose={onClose}
      ariaLabel="New note"
      testid="create-note-modal"
      contentClassName="max-h-[90vh] overflow-y-auto"
    >
      <div className="label-eyebrow mb-3">{clientName || "Client"}</div>
      <h2 className="font-heading text-2xl mb-6 tracking-tight">New note</h2>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="label-eyebrow block mb-2">Title</label>
          <input
            className="t-input !rounded-[var(--t-event-pill-radius)] bg-white/50 w-full"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            autoFocus
            data-testid="new-note-title-input"
          />
        </div>
        <div className="flex gap-3 pt-4">
          <button type="button" onClick={onClose} className="t-btn-secondary !rounded-[var(--t-event-pill-radius)] flex-1">
            Cancel
          </button>
          <button
            type="submit"
            className="neo-cta flex-1 justify-center"
            disabled={busy}
            data-testid="create-note-btn"
          >
            {busy ? "Creating…" : "Create note"}
          </button>
        </div>
      </form>
    </FullScreenModal>
  );
}

export default function PortalDocumentsPanel({
  embedded = false,
  layout = "page",
  refreshKey = 0,
  createOpen,
  onCreateOpenChange,
  hideBoardAddButton = false,
  hideAddButton = false,
  onNotesCountsChange,
}) {
  const { client, user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isBoardLayout = layout === "board";
  const suppressAddButton = hideAddButton || hideBoardAddButton;
  const canEditNote = useCallback(
    (note) => Boolean(isAdmin || (note?.created_by_user_id && note.created_by_user_id === user?.id)),
    [isAdmin, user?.id],
  );
  const canDeleteNote = useCallback(
    (note) => Boolean(note?.created_by_user_id && note.created_by_user_id === user?.id),
    [user?.id],
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(
    () => (isAdmin && client?.id ? `?client_id=${client.id}` : ""),
    [client?.id, isAdmin],
  );

  const [notes, setNotes] = useState([]);
  const filterParam = searchParams.get("filter");
  const filter = NOTE_FILTERS.has(filterParam) ? filterParam : "all";
  const [active, setActive] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [internalShowCreate, setInternalShowCreate] = useState(false);
  const showCreate = createOpen ?? internalShowCreate;
  const setShowCreate = onCreateOpenChange ?? setInternalShowCreate;
  const [shareOpen, setShareOpen] = useState(false);
  const [noteStatusMenuOpenId, setNoteStatusMenuOpenId] = useState(null);
  const [pendingDeleteNote, setPendingDeleteNote] = useState(null);
  const deepLinkNoteRef = useRef(null);
  const formRef = useRef(EMPTY_FORM);

  useEffect(() => {
    formRef.current = form;
  }, [form]);

  const noteToForm = useCallback(
    (note) => ({
      title: note?.title || "",
      content: note?.content || "",
      category: note?.category || "general",
      status: normalizeNoteStatus(note?.status),
      owner: note?.owner || "",
    }),
    [],
  );
  const load = useCallback(async () => {
    if (!client) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/portal/notes${params}`);
      const rows = sortNotesByCreatedDesc(
        (data || []).map((n) => ({ ...n, status: normalizeNoteStatus(n.status) })),
      );
      setNotes(rows);
      setActive((cur) => (cur ? rows.find((n) => n.id === cur.id) || null : null));
    } finally {
      setLoading(false);
    }
  }, [client, params]);

  const refreshNote = useCallback(
    async (noteId) => {
      if (!noteId) return null;
      try {
        const { data } = await api.get(`/portal/notes/${noteId}${params}`);
        const fresh = { ...data, status: normalizeNoteStatus(data.status) };
        setNotes((prev) => prev.map((n) => (n.id === fresh.id ? fresh : n)));
        setActive((cur) => (cur?.id === fresh.id ? fresh : cur));
        return fresh;
      } catch {
        return null;
      }
    },
    [params],
  );

  const openNote = useCallback(
    async (note) => {
      setActive(note);
      const fresh = await refreshNote(note.id);
      if (fresh) {
        setActive(fresh);
        setForm(noteToForm(fresh));
      }
    },
    [noteToForm, refreshNote],
  );

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  useEffect(() => {
    if (!isAdmin || !client || active) return undefined;
    const hasShared = notes.some((n) => normalizeNoteStatus(n.status) === "shared");
    if (!hasShared) return undefined;
    const timer = window.setInterval(() => {
      load();
    }, 12000);
    return () => window.clearInterval(timer);
  }, [isAdmin, client, active, notes, load]);

  useEffect(() => {
    const noteId = searchParams.get("note");
    if (!noteId) {
      deepLinkNoteRef.current = null;
      return;
    }
    // Auto-open deep links for admins and clients (shared notes only for clients).
    if (loading || notes.length === 0) return;
    if (deepLinkNoteRef.current === noteId) return;
    const match = notes.find((n) => n.id === noteId);
    if (!match) return;
    deepLinkNoteRef.current = noteId;
    openNote(match);
  }, [searchParams, notes, loading, openNote]);

  useEffect(() => {
    if (!active?.id || isAdmin) return undefined;
    const noteId = active.id;
    const timer = window.setInterval(() => {
      refreshNote(noteId);
    }, 4000);
    return () => window.clearInterval(timer);
  }, [active?.id, isAdmin, refreshNote]);

  useEffect(() => {
    if (!active) {
      setForm(EMPTY_FORM);
      return;
    }
    setForm(noteToForm(active));
  }, [active?.id, noteToForm]);

  const dirty = Boolean(
    active &&
      ((form.title || "") !== (active.title || "") || (form.content || "") !== (active.content || "")),
  );

  const activeCanEdit = active ? canEditNote(active) : false;

  useEffect(() => {
    if (!activeCanEdit || !active || !dirty) return undefined;
    const timer = window.setTimeout(async () => {
      try {
        const { data } = await api.patch(`/portal/notes/${active.id}${params}`, {
          title: form.title,
          content: form.content,
          category: form.category,
          status: form.status,
          owner: form.owner,
        });
        const saved = { ...data, status: normalizeNoteStatus(data.status) };
        setNotes((prev) => prev.map((n) => (n.id === saved.id ? saved : n)));
        setActive(saved);
      } catch {
        toast.error("Could not save note");
      }
    }, 650);
    return () => window.clearTimeout(timer);
  }, [dirty, form, params, active, activeCanEdit]);

  const patchForm = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const flushNoteSave = useCallback(async () => {
    if (!activeCanEdit || !active) return;
    const currentForm = formRef.current;
    const dirtyNow =
      (currentForm.title || "") !== (active.title || "") ||
      (currentForm.content || "") !== (active.content || "");
    if (!dirtyNow) return;
    const { data } = await api.patch(`/portal/notes/${active.id}${params}`, {
      title: currentForm.title,
      content: currentForm.content,
      category: currentForm.category,
      status: currentForm.status,
      owner: currentForm.owner,
    });
    const saved = { ...data, status: normalizeNoteStatus(data.status) };
    setNotes((prev) => prev.map((n) => (n.id === saved.id ? saved : n)));
    setActive(saved);
    setForm(noteToForm(saved));
  }, [active, activeCanEdit, noteToForm, params]);

  const closeNote = useCallback(async () => {
    try {
      await flushNoteSave();
    } catch {
      toast.error("Could not save note");
    }
    setShareOpen(false);
    setActive(null);
  }, [flushNoteSave]);

  const handleNoteShared = useCallback(
    (data) => {
      const saved = { ...data.note, status: normalizeNoteStatus(data.note?.status) };
      setNotes((prev) => prev.map((n) => (n.id === saved.id ? saved : n)));
      setShareOpen(false);
      setActive(null);
      toast.success(
        data.updated_only
          ? "Note updated — client notified"
          : data.email_sent
            ? `Note shared — email sent to ${data.to_email}`
            : `Note shared with ${data.to_email} (email not configured on server)`,
      );
    },
    [],
  );

  const updateNoteStatus = async (note, status) => {
    const next = normalizeNoteStatus(status);
    if (next === normalizeNoteStatus(note.status)) return;
    try {
      const { data } = await api.patch(`/portal/notes/${note.id}${params}`, { status: next });
      const saved = { ...data, status: normalizeNoteStatus(data.status) };
      setNotes((prev) => prev.map((n) => (n.id === saved.id ? saved : n)));
      if (active?.id === saved.id) setActive(saved);
    } catch {
      toast.error("Could not update status");
    }
  };

  const requestDeleteNote = (note, event) => {
    event?.stopPropagation();
    event?.preventDefault();
    setPendingDeleteNote(note);
  };

  const confirmDeleteNote = async () => {
    const note = pendingDeleteNote;
    if (!note) return;
    try {
      await api.delete(`/portal/notes/${note.id}${params}`);
      setNotes((prev) => prev.filter((n) => n.id !== note.id));
      if (active?.id === note.id) {
        setShareOpen(false);
        setActive(null);
      }
      setPendingDeleteNote(null);
      toast.success("Note deleted");
    } catch {
      toast.error("Could not delete note");
    }
  };

  const filtered = useMemo(() => {
    const list = notes.filter((n) => {
      const st = normalizeNoteStatus(n.status);
      if (filter === "all") return true;
      if (filter === "archived") return st === "archived";
      return true;
    });
    return sortNotesByCreatedDesc(list);
  }, [notes, filter]);

  useEffect(() => {
    onNotesCountsChange?.({ visible: filtered.length, total: notes.length });
  }, [filtered.length, notes.length, onNotesCountsChange]);

  const counts = {
    all: notes.length,
    archived: notes.filter((n) => normalizeNoteStatus(n.status) === "archived").length,
  };

  const setFilter = (key) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("filter", key);
      return next;
    }, { replace: true });
  };

  const filterButtons = isAdmin
    ? ADMIN_STATUS_FILTERS.map(([key, label]) => (
        <button
          key={key}
          type="button"
          onClick={() => setFilter(key)}
          className={`shrink-0 px-3 sm:px-4 py-3 ${NOTES_NAV_TEXT} transition-colors border-b-2 -mb-px whitespace-nowrap min-h-[44px] ${filter === key ? "border-[var(--t-accent)] text-[var(--t-text)]" : "border-transparent text-[var(--t-meta)] hover:text-[var(--t-text)]"}`}
          data-testid={`filter-${key.replace(/\s+/g, "-")}`}
        >
          {label} <span className="t-meta">({counts[key]})</span>
        </button>
      ))
    : null;

  if (!client) {
    if (isBoardLayout) return null;
    return <div className="p-12 t-meta">No client context.</div>;
  }

  const noteCard = (note) => (
    <div
      key={note.id}
      role="button"
      tabIndex={0}
      onClick={() => openNote(note)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          openNote(note);
        }
      }}
      className={cn(
        "group relative flex cursor-pointer flex-col neo-panel transition-transform duration-[160ms] ease-in-out hover:-translate-y-0.5",
        isBoardLayout ? "w-[240px] shrink-0 snap-start snap-always p-3 sm:w-[260px] min-h-[108px]" : "p-4 sm:p-6",
      )}
      data-testid={`note-${note.id}`}
    >
      {canDeleteNote(note) ? (
        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-[inherit] bg-white/70 opacity-0 transition-opacity group-hover:opacity-100">
          <button
            type="button"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => requestDeleteNote(note, e)}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[var(--t-border)] bg-white text-[var(--t-meta)] shadow-sm transition-colors hover:border-red-200 hover:text-red-600"
            aria-label={`Delete ${note.title || "note"}`}
            data-testid={`delete-note-${note.id}`}
          >
            <Trash2 size={16} strokeWidth={1.5} />
          </button>
        </div>
      ) : null}
      <div className={cn("flex min-w-0 flex-1 flex-col", isBoardLayout ? "mb-0" : "mb-2")}>
        <div className="min-w-0 flex-1">
          <div className={cn("flex min-w-0 flex-nowrap items-center gap-2 overflow-hidden sm:gap-3", isBoardLayout ? "mb-0.5" : "mb-1")}>
            <h3
              className={cn(
                "min-w-0 flex-1 truncate self-center font-heading !font-light leading-tight tracking-tight",
                isBoardLayout ? NOTES_BOARD_CARD_TITLE : NOTES_CARD_TITLE,
              )}
            >
              {note.title || "Untitled note"}
            </h3>
            {isAdmin && normalizeNoteStatus(note.status) !== "shared" ? (
              <NoteStatusPopover
                note={note}
                open={noteStatusMenuOpenId === note.id}
                onOpenChange={(open) => setNoteStatusMenuOpenId(open ? note.id : null)}
                onSelectStatus={updateNoteStatus}
              />
            ) : null}
          </div>
          <p
            className={cn(
              "font-light text-[var(--t-meta)]",
              isBoardLayout
                ? cn("line-clamp-2", NOTES_BOARD_CARD_BODY)
                : cn("mt-0.5 line-clamp-3 leading-normal", NOTES_NAV_TEXT),
            )}
          >
            {notePreviewFromContent(note.content)}
          </p>
        </div>
      </div>
      <div className="mt-auto flex items-end justify-end border-t border-transparent pt-2">
        <span
          className="pb-1 text-[10px] font-light leading-none tracking-[0.02em] text-[var(--t-meta)]"
          data-testid={`view-note-${note.id}`}
        >
          {noteActivityDisplay(note)}
        </span>
      </div>
    </div>
  );

  const modals = (
    <>
      {active && (
        <NoteDetailModal
          note={active}
          canEdit={activeCanEdit}
          form={form}
          onFormChange={patchForm}
          onClose={closeNote}
          onShare={isAdmin ? () => setShareOpen(true) : undefined}
        />
      )}

      {isAdmin && active && (
        <NoteShareDialog
          note={active}
          clientContactName={client?.primary_contact_name || ""}
          isUpdate={normalizeNoteStatus(active.status) === "shared"}
          clientQuery={params}
          open={shareOpen}
          onOpenChange={setShareOpen}
          onBeforeSend={flushNoteSave}
          onShared={handleNoteShared}
        />
      )}

      {showCreate && (
        <CreateNoteModal
          clientId={client.id}
          clientName={client.name}
          userName={user?.name}
          isAdmin={isAdmin}
          onClose={() => setShowCreate(false)}
          onSaved={(created) => {
            const normalized = { ...created, status: normalizeNoteStatus(created.status) };
            setNotes((prev) => sortNotesByCreatedDesc([normalized, ...prev]));
            setActive(normalized);
            if (normalized.status && normalized.status !== filter && filter !== "all") {
              setFilter(normalized.status);
            }
          }}
        />
      )}

      {pendingDeleteNote ? (
        <AlertDialog open={pendingDeleteNote !== null} onOpenChange={(open) => !open && setPendingDeleteNote(null)}>
          <AlertDialogContent
            className="z-[80] max-w-md border border-[var(--t-border)] bg-[var(--t-bg)] text-[var(--t-text)] shadow-none sm:rounded-[var(--t-event-pill-radius)] gap-5"
            data-testid="note-delete-confirm-dialog"
          >
            <AlertDialogHeader>
              <AlertDialogTitle className="font-heading text-xl font-light tracking-tight text-[var(--t-text)]">
                Delete this note?
              </AlertDialogTitle>
              <AlertDialogDescription className="text-sm font-light text-[var(--t-meta)]">
                {pendingDeleteNote ? (
                  <>
                    <span className="text-[var(--t-text)]">&ldquo;{pendingDeleteNote.title || "Untitled note"}&rdquo;</span>{" "}
                    will be permanently removed. You cannot undo this.
                  </>
                ) : null}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="gap-3 sm:justify-end">
              <AlertDialogCancel className="t-btn-secondary min-h-[44px] border-[var(--t-border)] bg-[var(--t-bg)] text-[var(--t-text)] hover:opacity-90">
                Cancel
              </AlertDialogCancel>
              <button
                type="button"
                className="neo-cta"
                data-testid="note-delete-confirm-btn"
                onClick={confirmDeleteNote}
              >
                Delete
              </button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      ) : null}
    </>
  );

  if (isBoardLayout) {
    const notesList = (
      <div className="min-w-0 flex-1" data-testid="notes-list">
        {loading ? (
          <div className="t-meta py-2 text-sm">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="py-2 text-sm text-[var(--t-meta)]">
            {isAdmin ? "No notes yet." : "No notes yet."}
          </div>
        ) : (
          <div className="flex gap-3 overflow-x-auto pb-1 snap-x snap-mandatory [-webkit-overflow-scrolling:touch] [scrollbar-width:thin]">
            {filtered.map(noteCard)}
          </div>
        )}
      </div>
    );

    return (
      <div className="min-w-0" data-testid="board-documents-panel">
        {isAdmin && !suppressAddButton ? (
          <div className="mb-3 flex min-w-0 items-start gap-3">
            {notesList}
            <button
              type="button"
              onClick={() => setShowCreate(true)}
              className="neo-cta shrink-0"
              data-testid="new-note-btn"
            >
              <Plus size={14} strokeWidth={1.4} /> Add note
            </button>
          </div>
        ) : (
          notesList
        )}

        {modals}
      </div>
    );
  }

  return (
    <div className={embedded ? "t-fade-up min-w-0" : "p-4 sm:p-5 md:p-7 t-fade-up min-w-0"} data-testid="notes-page">
      {!embedded && (
        <>
          <div className="label-eyebrow mb-2">Studio</div>
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-end gap-4 mb-10">
            <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl tracking-tight">Notes.</h1>
            {!suppressAddButton ? (
              <button
                type="button"
                onClick={() => setShowCreate(true)}
                className="neo-cta self-start"
                data-testid="new-note-btn"
              >
                <Plus size={16} strokeWidth={1.4} /> Add note
              </button>
            ) : null}
          </div>
        </>
      )}

      {embedded ? (
        <div className="mb-6 flex flex-row items-end justify-between gap-3 min-w-0">
          {isAdmin ? (
            <div
              className="flex min-w-0 flex-1 gap-1 border-b border-[var(--t-border)] overflow-x-auto [-webkit-overflow-scrolling:touch]"
              data-testid="note-filters"
            >
              {filterButtons}
            </div>
          ) : (
            <PortalNotesRatioStat visibleCount={filtered.length} totalCount={notes.length} embedded />
          )}
          {!suppressAddButton ? (
            <button
              type="button"
              onClick={() => setShowCreate(true)}
              className="neo-cta shrink-0"
              data-testid="new-note-btn"
            >
              <Plus size={16} strokeWidth={1.4} /> Add note
            </button>
          ) : null}
        </div>
      ) : isAdmin ? (
        <div
          className="flex gap-1 mb-6 border-b border-[var(--t-border)] overflow-x-auto min-w-0 [-webkit-overflow-scrolling:touch]"
          data-testid="note-filters"
        >
          {filterButtons}
        </div>
      ) : (
        <div className="mb-6" data-testid="note-filters">
          <PortalNotesRatioStat visibleCount={filtered.length} totalCount={notes.length} embedded={false} />
        </div>
      )}

      <div className="space-y-3" data-testid="notes-list">
        {loading ? (
          <div className="neo-panel t-meta text-center py-12">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="neo-panel t-meta text-center py-12">
            {isAdmin ? "No notes yet." : "No notes yet."}
          </div>
        ) : (
          filtered.map(noteCard)
        )}
      </div>

      {modals}
    </div>
  );
}
